import { Table, Tag, Dropdown } from "@/config/antd";
import CustomMoreOptions from "@/components/specific/Custom_More_Options";
import "@/assets/styles/custom_table.css";
import PropTypes from "prop-types";
import Column from "antd/es/table/Column";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchEventByAny,
  selectEventLoading,
  selectFetchedEvents,
} from "@/store/redux/slices/event/event_slice";
import Delete from "@/components/common/alerts/Delete";
import WebIcon from "@/components/common/Web_Icons";
import { calculateEventCompletionPercentage } from "@/utils/event_utils";
import { getOrganizationId } from "@/utils/token_functions";
import { isScheduleExpired } from "@/utils/calendar_utils";
import { selectSearchTerm } from "@/store/redux/slices/search/search_slice";

const EventHomeTable = ({
  setSelectedEvents,
  currentPage,
  pageSize,
  onDelete,
  onModify,
  filteredEvents,
  activeFilter,
  simplified = false,
  skipFetch = false,
}) => {
  const dispatch = useDispatch();
  const fetchedEventsByOrg = useSelector(selectFetchedEvents);
  const isLoading = useSelector(selectEventLoading);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const globalSearchTerm = useSelector(selectSearchTerm);

  useEffect(() => {
    if (skipFetch) return;

    const organizationID = getOrganizationId();

    dispatch(
      fetchEventByAny({
        key: "organizationID",
        value: organizationID,
      })
    );

    // dispatch(fetchEvents({ skip, limit: pageSize }));
  }, [dispatch, currentPage, pageSize, skipFetch]);

  const handleMenuClick = (menuItem, record) => {
    if (menuItem.key === "delete") {
      setSelectedEvent(record);
      setDeleteModalVisible(true);
    } else if (menuItem.key === "modify") {
      onModify(record._id);
    }
  };

  const handleDeleteConfirm = () => {
    if (selectedEvent) {
      onDelete(selectedEvent._id, selectedEvent.title);
      setDeleteModalVisible(false);
      setSelectedEvent(null);
    }
  };

  // rowSelection object indicates the need for row selection
  const rowSelection = {
    onChange: (selectedRowKeys) => {
      setSelectedEvents(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.name === "Disabled User",
      // Column configuration not to be checked
      name: record.name,
    }),
  };

  // Update tableData to apply global search filter
  const applyGlobalSearch = (events) => {
    if (!globalSearchTerm) return events;

    return events.filter(
      (event) =>
        event.name?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
        event.type?.toLowerCase().includes(globalSearchTerm.toLowerCase()) ||
        event.location?.some((loc) =>
          loc.toLowerCase().includes(globalSearchTerm.toLowerCase())
        )
    );
  };

  // Update the existing tableData construction to use the global search
  const tableData = applyGlobalSearch(
    skipFetch
      ? filteredEvents
      : activeFilter === "all" && fetchedEventsByOrg.length > 0
      ? fetchedEventsByOrg
      : filteredEvents
  ).map((event) => {
    // Calculate percentage dynamically
    const percentage = calculateEventCompletionPercentage(event);

    // Local end-of-day + endTime (same as SR) — avoids UTC-midnight false expiry
    const currentDate = new Date();
    const isExpired = isScheduleExpired(event.schedule, currentDate);

    return {
      key: event._id,
      percentage,
      id: event.id || event._id,
      _id: event._id,
      title: event.name,
      publishDate: new Date(event.createdAt).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      }),
      duration: {
        startDate: event.schedule?.startDate || "",
        startTime: event.schedule?.startTime || "",
        endDate: event.schedule?.endDate || "",
        endTime: event.schedule?.endTime || "",
      },
      locations: event.location || [],
      type: event.type || "Unknown",
      status: isExpired ? "Expired" : "On Going",
      isExpired,
    };
  });

  return (
    <>
      <Table
        loading={isLoading}
        rowSelection={
          simplified
            ? false
            : {
                type: "checkbox",
                ...rowSelection,
              }
        }
        dataSource={tableData}
        pagination={false}
        onRow={(record) => ({
          onClick: () => {}, // Empty click handler to prevent row click navigation
          className: record.isExpired ? "opacity-60" : "",
        })}
        rowKey={(record) => record._id}>
        <>
          <Column
            title="Percentage"
            dataIndex="percentage"
            key="percentage"
            render={(_, { _id, percentage, isExpired }) => (
              <Link
                to={`/dashboard/events/${_id}`}
                className="relative flex-shrink-0 block w-14 h-14 sm:w-16 sm:h-16">
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="50%"
                    cy="50%"
                    r="45%"
                    stroke="#E5E7EB"
                    strokeWidth="4"
                    fill="none"
                  />
                  <circle
                    cx="50%"
                    cy="50%"
                    r="45%"
                    stroke={
                      isExpired
                        ? "var(--veegil-color-danger, #FF4D4F)"
                        : "#0F75BC"
                    }
                    strokeWidth="4"
                    fill="none"
                    strokeDasharray={175.93}
                    strokeDashoffset={175.93 * (1 - percentage / 100)}
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span
                    className={`text-base font-semibold sm:text-lg ${
                      isExpired ? "text-danger" : "text-primary"
                    }`}>
                    {percentage}%
                  </span>
                </div>
              </Link>
            )}
          />
          <Column
            title="Name"
            dataIndex="name"
            key="name"
            render={(_, { id, _id, title, publishDate }) => (
              <Link
                to={`/dashboard/events/${_id}`}
                className="flex flex-col justify-start text-left align-start whitespace-nowrap">
                <span className="text-small sm:text-[10px] text-primary">
                  #{id}
                </span>
                <h3 className="mt-1 font-bold text-bold sm:text-base text-darkGrey truncate max-w-[200px]">
                  {title}
                </h3>
                <div className="flex items-center mt-2 text-darkGrey text-small sm:text-sm">
                  <WebIcon
                    icon="CalenderFilled"
                    size="small"
                    className="mr-1"
                  />
                  <span className="text-small text-lighterGrey font-bold">
                    Published on {publishDate}
                  </span>
                </div>
              </Link>
            )}
          />
          <Column
            title="Duration"
            dataIndex="duration"
            key="duration"
            render={(_, { _id, duration }) => (
              <Link to={`/dashboard/events/${_id}`} className="flex gap-5">
                <div>
                  <h3 className="text-darkGrey text-xs">Starting</h3>
                  <p className="text-regular font-bold">
                    {duration?.startDate}
                  </p>
                  <p className="text-regular font-bold">
                    {duration?.startTime}
                  </p>
                </div>
                <div>
                  <h3 className="text-darkGrey text-xs">Ending</h3>
                  <p className="text-regular font-bold">{duration?.endDate}</p>
                  <p className="text-regular font-bold">{duration?.endTime}</p>
                </div>
              </Link>
            )}
          />
          <Column
            title="Locations"
            dataIndex="locations"
            key="locations"
            render={(_, { _id, locations }) => (
              <>
                {locations?.map((location) => {
                  let color = location.length > 5 ? "geekblue" : "green";
                  if (location === "loser") {
                    color = "volcano";
                  }
                  return (
                    <Tag
                      color={color}
                      className="text-small font-bold truncate w-[100px]"
                      key={location}>
                      <Link to={`/dashboard/events/${_id}`}>
                        {location.toUpperCase()}
                      </Link>
                    </Tag>
                  );
                })}
              </>
            )}
          />
          {!simplified && (
            <>
              <Column
                title="Type"
                key="type"
                dataIndex="type"
                render={(_, { _id, type }) => (
                  <Link
                    to={`/dashboard/events/${_id}`}
                    className="text-regular font-bold">
                    {type}
                  </Link>
                )}
              />
              <Column
                title="Status"
                key="status"
                dataIndex="status"
                render={(_, { _id, status, isExpired }) => (
                  <Link
                    to={`/dashboard/events/${_id}`}
                    className="flex items-center gap-1">
                    <div
                      className={`w-2 h-2 rounded-full ${
                        isExpired ? "bg-danger" : "bg-primary"
                      }`}
                    />
                    <p
                      className={`text-small font-bold whitespace-nowrap ${
                        isExpired ? "text-danger" : "text-primary"
                      }`}>
                      {status}
                    </p>
                  </Link>
                )}
              />
              <Column
                title=""
                key="action"
                render={(_, record) => (
                  <Dropdown
                    menu={{
                      items: [
                        {
                          key: "modify",
                          label: "Modify",
                          onClick: (e) => {
                            e.domEvent.stopPropagation();
                            handleMenuClick({ key: "modify" }, record);
                          },
                        },
                        {
                          key: "delete",
                          label: "Delete",
                          onClick: (e) => {
                            e.domEvent.stopPropagation();
                            handleMenuClick({ key: "delete" }, record);
                          },
                        },
                      ],
                    }}
                    trigger={["click"]}>
                    <CustomMoreOptions
                      variant="filled"
                      onClick={(e) => e.stopPropagation()}
                      color="black"
                      orientation="vertical"
                      size="small"
                    />
                  </Dropdown>
                )}
              />
            </>
          )}
        </>
      </Table>

      <Delete
        isVisible={deleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        onProceed={handleDeleteConfirm}
        header="Delete Event?"
        paragraph={
          selectedEvent
            ? `Are you sure you want to delete the event "${selectedEvent.title}" from your existing list?`
            : ""
        }
        proceedButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </>
  );
};

EventHomeTable.propTypes = {
  setSelectedEvents: PropTypes.func,
  currentPage: PropTypes.number,
  pageSize: PropTypes.number,
  onDelete: PropTypes.func,
  onModify: PropTypes.func,
  filteredEvents: PropTypes.array,
  activeFilter: PropTypes.string,
  simplified: PropTypes.bool,
  skipFetch: PropTypes.bool,
};

export default EventHomeTable;
